import { createContext, useContext, useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { APP_URL, AUTH_URL } from "@/lib/urls";
import { setReferralCookie, getReferralCookie, deleteReferralCookie } from "@/lib/referral-cookie";
import { useTranslation } from "@/lib/i18n";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { authClient } from "@/lib/better-auth";
import { AuthDialog } from "./AuthDialog";
import { AuthDrawer } from "./AuthDrawer";

const checkCodeAvailability = async (code: string) => {
  const response = await fetch(
    `${AUTH_URL}/referral-code?referralCode=${code}`
  );
  const data = (await response.json()) as {
    existingCode: { code_meta?: string | { extraTrialDays?: number } | null } | null;
    isValid: boolean;
  };
  return data;
};

const getExtraTrialDaysFromCode = (
  existingCode: { code_meta?: string | { extraTrialDays?: number } | null } | null,
): number => {
  if (!existingCode?.code_meta) return 0;
  try {
    const meta =
      typeof existingCode.code_meta === "string"
        ? (JSON.parse(existingCode.code_meta) as { extraTrialDays?: number })
        : existingCode.code_meta;
    return meta?.extraTrialDays ?? 0;
  } catch {
    return 0;
  }
};

type AuthContextType = {
  openAuth: () => void;
  closeAuth: () => void;
  isOpen: boolean;
  isRedirecting: boolean;
  isAuthenticated: boolean;
  isAuthResolved: boolean;
  referralCode: string;
  isReferralValid: boolean;
  showReferralBanner: boolean;
  extraTrialDays: number;
  verifyReferralCode: (code: string) => Promise<boolean>;
  clearReferral: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { translations: t } = useTranslation();
  const isMobileAuth = useMediaQuery("(max-width: 767px)");
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { data: session, isPending: isSessionPending } = authClient.useSession();
  const isAuthenticated = Boolean(session?.user);
  const isAuthResolved = !isSessionPending;
  const isRedirecting = false;
  const [referralCode, setReferralCode] = useState("");
  const [isReferralValid, setIsReferralValid] = useState(false);
  const [showReferralBanner, setShowReferralBanner] = useState(false);
  const [extraTrialDays, setExtraTrialDays] = useState(0);
  const hasSessionRef = useRef(false);

  // Check for referral code in URL, cookie, or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinusCode = urlParams.get("joinus");

    if (joinusCode) {
      // Auto-validate referral code from URL and set cookie only if valid
      const code = joinusCode.trim();
      setReferralCode(code);
      // Validate and set cookie (async, fire and forget)
      checkCodeAvailability(code).then((data) => {
        if (data.isValid) {
          setIsReferralValid(true);
          setShowReferralBanner(true);
          const days = getExtraTrialDaysFromCode(data.existingCode);
          setExtraTrialDays(days);
          localStorage.setItem("referralCode", code);
          // Set cookie with Domain=.zapify.pro for cross-subdomain access
          setReferralCookie(code);
        }
      }).catch(() => {
        // Silently fail - user can still manually apply later
      });
    } else {
      // Check cookie first (works across subdomains), then localStorage
      // These are already validated codes from previous sessions
      const cookieCode = getReferralCookie();
      const storedCode = localStorage.getItem("referralCode");
      const code = cookieCode || storedCode;
      if (code) {
        setReferralCode(code);
        // If we have a cookie, the code was already validated
        if (cookieCode) {
          setIsReferralValid(true);
        }
      }
    }
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    hasSessionRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const openAuth = async () => {
    let hasSession = hasSessionRef.current;

    if (!isAuthResolved) {
      try {
        const latestSession = await authClient.getSession();
        hasSession = Boolean(latestSession.data);
        hasSessionRef.current = hasSession;
      } catch {
        hasSession = false;
        hasSessionRef.current = false;
      }
    }

    // If user has a session, redirect directly to dashboard
    if (hasSession) {
      const redirectUrl = referralCode && isReferralValid
        ? `${APP_URL}/app/onboarding?joinus=${referralCode}`
        : `${APP_URL}/app/dashboard`;
      window.location.href = redirectUrl;
      return;
    }

    // Store referral code before opening auth
    if (referralCode && isReferralValid) {
      localStorage.setItem("pendingReferral", referralCode);
    }
    setIsOpen(true);
  };
  const closeAuth = () => setIsOpen(false);

  const clearReferral = () => {
    setReferralCode("");
    setIsReferralValid(false);
    setShowReferralBanner(false);
    setExtraTrialDays(0);
    localStorage.removeItem("referralCode");
    localStorage.removeItem("pendingReferral");
    deleteReferralCookie();
  };

  const verifyReferralCode = async (code: string): Promise<boolean> => {
    try {
      const data = await checkCodeAvailability(code);
      if (data.isValid) {
        setReferralCode(code);
        setIsReferralValid(true);
        setShowReferralBanner(true);
        const days = getExtraTrialDaysFromCode(data.existingCode);
        setExtraTrialDays(days);
        localStorage.setItem("referralCode", code);
        // Set cookie with Domain=.zapify.pro for cross-subdomain access
        setReferralCookie(code);
        return true;
      }
      setIsReferralValid(false);
      setExtraTrialDays(0);
      return false;
    } catch {
      setIsReferralValid(false);
      setExtraTrialDays(0);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      openAuth,
      closeAuth,
      isOpen,
      isRedirecting,
      isAuthenticated,
      isAuthResolved,
      referralCode,
      isReferralValid,
      showReferralBanner,
      extraTrialDays,
      verifyReferralCode,
      clearReferral
    }}>
      {children}
      {/* Referral Success Banner */}
      {showReferralBanner && isReferralValid && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2 px-3 shadow-lg">
          <div className="container mx-auto flex items-center justify-center gap-1.5">
            <span className="text-sm">🎉</span>
            <p className="text-xs font-medium">
              {extraTrialDays > 0
                ? t.auth.extendedTrialBannerText.replace("{days}", String(30 + extraTrialDays))
                : t.auth.referralBannerText}
            </p>
            <button
              onClick={() => setShowReferralBanner(false)}
              className="ml-3 text-white/80 hover:text-white transition-colors"
              aria-label="Close banner"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {isRedirecting && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
            <p className="text-xs text-muted-foreground">{t.auth.redirectingToDashboard}</p>
          </div>
        </div>
      )}
      {isMounted && isOpen && (
        isMobileAuth ? (
          <AuthDrawer open={isOpen} onOpenChange={setIsOpen} />
        ) : (
          <AuthDialog open={isOpen} onOpenChange={setIsOpen} />
        )
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
